import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Briefcase, TrendingUp, TrendingDown } from 'lucide-react';
import { usePortfolioSnapshot, useEquityCurve, usePositions } from '@/hooks/useApi';
import { StatCard, Card, LoadingSpinner, Badge } from '@/components/UI';
import { formatCurrency, formatPercent, pnlColor } from '@/utils';
import type { Position, EquityPoint } from '@/types';

export function PortfolioPage() {
  const { data: snapshot, isLoading: snapLoading } = usePortfolioSnapshot();
  const { data: equityCurve, isLoading: curveLoading } = useEquityCurve(undefined, 30);
  const { data: positions, isLoading: posLoading } = usePositions();

  if (snapLoading) return <LoadingSpinner text="Loading portfolio..." />;

  const chartData = (equityCurve ?? []).map((p: EquityPoint) => ({
    date: new Date(p.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    equity: p.equity,
    balance: p.balance,
    unrealized: p.unrealized_pnl,
  }));

  return (
    <div className="space-y-6 animate-in">
      <h1 className="text-2xl font-bold text-surface-100">Portfolio</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Value"
          value={formatCurrency(snapshot?.total_value ?? 0)}
          icon={<Briefcase className="w-5 h-5" />}
        />
        <StatCard
          label="Available Balance"
          value={formatCurrency(snapshot?.available_balance ?? 0)}
        />
        <StatCard
          label="Unrealized P&L"
          value={formatCurrency(snapshot?.unrealized_pnl ?? 0)}
          icon={snapshot?.unrealized_pnl !== undefined && snapshot.unrealized_pnl >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
        />
        <StatCard
          label="Realized P&L"
          value={formatCurrency(snapshot?.realized_pnl ?? 0)}
        />
      </div>

      {/* Equity Curve */}
      <Card title="Equity Curve" subtitle="30-day performance">
        <div className="h-72">
          {curveLoading ? (
            <LoadingSpinner />
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-surface-500">
              No equity data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    color: '#e2e8f0',
                  }}
                />
                <Area type="monotone" dataKey="equity" stroke="#6366f1" fill="url(#equityGrad)" strokeWidth={2} />
                <Line type="monotone" dataKey="balance" stroke="#475569" strokeDasharray="5 5" strokeWidth={1} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Positions */}
      <Card title="Open Positions" subtitle={`${positions?.length ?? 0} positions`}>
        {posLoading ? (
          <LoadingSpinner />
        ) : !positions || positions.length === 0 ? (
          <div className="text-sm text-surface-500 text-center py-8">
            No open positions
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Qty</th>
                  <th>Entry</th>
                  <th>Current</th>
                  <th>P&L</th>
                  <th>P&L %</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos: Position) => (
                  <tr key={pos.id}>
                    <td className="font-medium text-surface-200">{pos.symbol}</td>
                    <td>
                      <Badge variant={pos.side === 'long' ? 'profit' : 'loss'}>
                        {pos.side.toUpperCase()}
                      </Badge>
                    </td>
                    <td>{pos.quantity}</td>
                    <td>{formatCurrency(pos.entry_price)}</td>
                    <td>{formatCurrency(pos.current_price)}</td>
                    <td className={pnlColor(pos.unrealized_pnl)}>
                      {formatCurrency(pos.unrealized_pnl)}
                    </td>
                    <td className={pnlColor(pos.unrealized_pnl_pct)}>
                      {formatPercent(pos.unrealized_pnl_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
